import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { MessageItem } from './MessageItem';
import { ProductRecommendations } from './ProductRecommendations';
import type { Message } from './types';

interface MessageListProps {
  messages?: Message[];
  onDiscountResponse: (
    messageId: string,
    action: 'approved' | 'declined',
    newPrice?: number
  ) => void;
  editingDiscount: { messageId: string | null; newPrice: string };
  setEditingDiscount: (state: {
    messageId: string | null;
    newPrice: string;
  }) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoadingInitial?: boolean;
}

export function MessageList({
  messages,
  onDiscountResponse,
  editingDiscount,
  setEditingDiscount,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoadingInitial,
}: MessageListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // Scroll to bottom on initial load or new message
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior,
      });
    }
  }, []);

  // Infinite scroll for old messages (scroll to top)
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !isLoadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element || !onLoadMore) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver, onLoadMore]);

  // Reset on messages change (new chat selected)
  useEffect(() => {
    if (!messages || messages.length === 0) {
      isInitialLoadRef.current = true;
      return;
    }

    // On initial load, scroll to bottom instantly
    if (isInitialLoadRef.current) {
      setTimeout(() => scrollToBottom('instant'), 100);
      isInitialLoadRef.current = false;
      lastMessageCountRef.current = messages.length;
      return;
    }

    // If new messages added (not from loading more), scroll to bottom
    if (messages.length > lastMessageCountRef.current) {
      setTimeout(() => scrollToBottom('smooth'), 100);
    }

    lastMessageCountRef.current = messages.length;
  }, [messages, scrollToBottom]);

  if (isLoadingInitial) {
    return (
      <div
        className="flex-1 overflow-y-hidden px-4 py-3 bg-gradient-to-b from-background to-muted/20"
        style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex gap-3 animate-pulse">
              <div className="h-10 w-10 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 rounded-full bg-muted/70" />
                <div className="h-3 w-2/3 rounded-full bg-muted/50" />
                <div className="h-3 w-1/2 rounded-full bg-muted/40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto px-4 py-3 bg-gradient-to-b from-background to-muted/20"
      style={{ maxHeight: 'calc(100vh - 280px)' }}
    >
      {/* Load more trigger at top */}
      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-3 mb-2">
          {isLoadingMore && (
            <div className="flex items-center gap-2 text-muted-foreground bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs font-medium">
                Loading older messages...
              </span>
            </div>
          )}
        </div>
      )}

      {/* Messages with grouping */}
      <div className="space-y-2">
        {messages.map((message, index) => {
          const prevMessage = index > 0 ? messages[index - 1] : null;
          const isSameSender = prevMessage?.isAdmin === message.isAdmin;
          const timeDiff = prevMessage
            ? new Date(message.createdAt).getTime() -
              new Date(prevMessage.createdAt).getTime()
            : 0;
          const isGrouped = isSameSender && timeDiff < 60000; // Group if within 1 minute

          const hasInfoOrQuestion = !!(message.information || message.question);

          return (
            <div
              key={message.id}
              className={isGrouped ? 'mt-1 space-y-1' : 'mt-3 space-y-1'}
            >
              {/* If message has information/question: render info -> products -> question */}
              {hasInfoOrQuestion ? (
                <>
                  {message.information && (
                    <MessageItem
                      key={`${message.id}-info`}
                      message={{
                        ...message,
                        id: `${message.id}-info`,
                        text: message.information,
                        information: null,
                        question: null,
                        options: null,
                      }}
                      onDiscountResponse={onDiscountResponse}
                      editingDiscount={editingDiscount}
                      setEditingDiscount={setEditingDiscount}
                    />
                  )}

                  {message.products && message.products.length > 0 && (
                    <div
                      className={`mt-2 ${message.isAdmin ? 'mr-2' : 'ml-2'}`}
                    >
                      <ProductRecommendations
                        // @ts-expect-error - message.products is not typed
                        products={message.products}
                        isAdmin={message.isAdmin}
                        onProductClick={(product) => {
                          // Handle product click (e.g., open product details)
                          console.log('Product clicked:', product);
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onDiscountRequest={(product: any) => {
                          // Handle discount request
                          console.log('Discount requested for:', product);
                        }}
                      />
                    </div>
                  )}

                  {message.question && (
                    <MessageItem
                      key={`${message.id}-question`}
                      message={{
                        ...message,
                        id: `${message.id}-question`,
                        text: message.question,
                        information: null,
                        question: null,
                        options: null,
                      }}
                      onDiscountResponse={onDiscountResponse}
                      editingDiscount={editingDiscount}
                      setEditingDiscount={setEditingDiscount}
                    />
                  )}
                </>
              ) : (
                <>
                  <MessageItem
                    message={message}
                    onDiscountResponse={onDiscountResponse}
                    editingDiscount={editingDiscount}
                    setEditingDiscount={setEditingDiscount}
                  />

                  {message.products && message.products.length > 0 && (
                    <div
                      className={`mt-2 ${message.isAdmin ? 'mr-2' : 'ml-2'}`}
                    >
                      <ProductRecommendations
                        // @ts-expect-error - message.products is not typed
                        products={message.products}
                        isAdmin={message.isAdmin}
                        onProductClick={(product) => {
                          // Handle product click (e.g., open product details)
                          console.log('Product clicked:', product);
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onDiscountRequest={(product: any) => {
                          // Handle discount request
                          console.log('Discount requested for:', product);
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
